import React, { useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import RoundedButton from "../../components/Buttons/RoundedButton/RoundedButton";
import axios from "axios";
import { backend } from "../../backend";
import "./request.css";
import SEOTags from "../../utils/SEOTags";
import Team from "../../assets/illustrations/team2.png";
import { useAutoExpand } from "../../customHooks/useAutoExpand";
import Layout from "../../components/layout/Layout";
import { validateOptionalPhoneNumber } from "../../utils/validators";

const BreadcrumbListSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      item: { "@id": "https://www.vakansa.se/", name: "Hem" },
    },
    {
      "@type": "ListItem",
      position: 2,
      item: {
        "@id": `https://www.vakansa.se/lokalforslag`,
        name: "Lokalförslag",
      },
    },
  ],
};

export default function Request() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [otherInputs, setOtherInputs] = useState({
    typAvRum: false,
    andraTider: false,
  });

  const autoExpand = useAutoExpand();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const selectedKontraktForm = useWatch({
    name: "kontraktForm",
    control,
  });
  const selectedOrganisationsTyp = useWatch({
    name: "organisationsTyp",
    control,
  });
  const selectedPrisTyp = useWatch({
    name: "prisTyp",
    control,
  });
  const selectedmoms = useWatch({
    name: "moms",
    control,
  });

  const onSubmit = async (formData) => {
    try {
      const success = await handleFormSubmit(formData);
      if (success) {
        window.scrollTo(0, 0);
      }
    } catch (error) {
      setErrorMessage("Ett fel uppstod, försök igen senare.");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = async (formData) => {
    try {
      const response = await axios.post(
        backend("/api/contact/space-request"),
        formData,
      );
      if (response.status === 201) {
        setFormSubmitted(true);
      } else {
        setErrorMessage(
          response.data.msg || "Ett fel uppstod, försök igen senare.",
        );
      }
    } catch (error) {
      if (error.response) {
        setErrorMessage(
          error.response.data.msg || "Ett fel uppstod, försök igen senare.",
        );
      } else {
        setErrorMessage("Ett fel uppstod, försök igen senare.");
      }
    }
  };

  if (formSubmitted) {
    return (
      <div className="request-form-section-requested">
        <h2>Tack för din lokalförfrågan!</h2>
        <p>
          Din förfrågan ses över och vi återkommer inom kort med specifika
          lokalförslag som passar dina behov.
        </p>
        <img
          className="intro-img-requested"
          src={Team}
          alt="Vakansa logo over four illustrated colleagues standing together—one holding a book and another a smartphone"
        />
      </div>
    );
  }

  const orgValidator = (organization) => {
    return /^[0-9]{6}-?[0-9]{4}$/.test(organization);
  };

  // const orgNumber = watch("organisationNumber", "");

  const orgNumberMasking = (e) => {
    const value = e.target.value;
    const cleanValue = value.replace(/[^0-9]+/g, "");

    const sliced = cleanValue.slice(0, 10);
    const formatted =
      sliced.length > 6 ? `${sliced.slice(0, 6)}-${sliced.slice(6)}` : sliced;

    setValue("organisationNumber", formatted, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <>
      <SEOTags
        title={"Få upp till 3 kostnadsfria lokalförslag via Vakansa!   "}
        description={
          "Söker du lokal? Vakansa hjälper dig att hitta lokalerna som uppfyller just dina behov! Fyll i vårt formulär & få 3st konkreta lokalförslag gratis!"
        }
        canonicalUrl={"/lokalforslag"}
        ogImage={"/images/logo/meta-logo.jpg"}
        structuredData={BreadcrumbListSchema}
      />
      <Layout>
        <div className="request-form-section">
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <div>
              <h1>Lokalförslag</h1>
              <h2>Vakansa återkommer med lokaler baserat på dina behov!</h2>
              <p className="request-form-subheading">
                Önskar du lokalförslag?
                <br />
                Sluta leta! Få skräddarsydda lokalförslag till er inom några
                dagar - helt kostnadsfritt! <b>Spara värdefull tid</b> och få 
                <b>konkreta förslag</b> med <b>tydliga priser</b> som matchar
                dina behov. Dessutom får du tillgång till 
                <b>hundratals dolda objekt</b>
                 som vi på Vakansa har tillgång till genom våra databaser.
              </p>
              <p>
                Genom att besvara nedan formulär kring vilken typ av lokal du
                söker, hur många ni är, om ni har en momsad eller omomsad
                verksamhet (läs vår artikel om omomsad verksamhet här)
                <br />
                <br />
                1. Beskriv dina behov här nedan på <b>2 minuter</b>.
                <br />
                2. Få 3st <b>konkreta lokalförslag</b> som matchar dina behov
                inom några dagar, kostnadsfritt.
              </p>
            </div>
            <div className="reqest-form-input-fields">
              <div className="request-form-name">
                <label>
                  Ditt namn*
                  <input
                    id="name"
                    {...register("namn", { required: true })}
                    aria-invalid={errors.namn ? "true" : "false"}
                    aria-describedby={errors.namn ? "name-error" : undefined}
                  />
                  {errors.namn && (
                    <span id="name-error" role="alert">
                      Vänligen ange ett namn
                    </span>
                  )}
                </label>
              </div>
              <div className="request-form-email-number">
                <label>
                  E-postadress*
                  <input
                    type="email"
                    {...register("email", {
                      required: "Vänligen ange en e-postadress",
                      pattern: {
                        value:
                          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                        message: "Vänligen ange en giltig e-postadress",
                      },
                    })}
                    aria-invalid={errors.email ? "true" : "false"}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <span id="email-error" role="alert">
                      {errors.email.message}
                    </span>
                  )}
                </label>
                <label>
                  Telefon
                  <input
                    {...register("telefonnummer", {
                      validate: validateOptionalPhoneNumber,
                    })}
                    aria-invalid={errors.telefonnummer ? "true" : "false"}
                    aria-describedby={
                      errors.telefonnummer ? "phone-error" : undefined
                    }
                  />
                  {errors.telefonnummer && (
                    <span id="phone-error" role="alert">
                      {errors.telefonnummer.message}
                    </span>
                  )}
                </label>
              </div>

              <div className="request-form-email-number">
                <label>
                  Organisationen du representerar*
                  <input
                    {...register("organisation", { required: true })}
                    aria-invalid={errors.organisation ? "true" : "false"}
                    aria-describedby={
                      errors.organisation ? "org-error" : undefined
                    }
                  />
                  {errors.organisation && (
                    <span id="org-error" role="alert">
                      Vänligen ange en organisation
                    </span>
                  )}
                </label>
                <label>
                  Organisationsnummer*
                  <input
                    {...register("organisationNumber", {
                      required: "Vänligen ange ett organisationsnummer",
                      validate: (value) =>
                        orgValidator(value) ||
                        "Org.nr måste vara i formatet XXXXXX-XXXX",
                    })}
                    onChange={orgNumberMasking}
                    // value={orgNumber}
                    placeholder="000000-0000"
                    aria-invalid={errors.organisationNumber ? "true" : "false"}
                    aria-describedby={
                      errors.organisationNumber ? "org-number-error" : undefined
                    }
                  />
                  {errors.organisationNumber && (
                    <span id="org-number-error" role="alert">
                      {errors.organisationNumber.message}
                    </span>
                  )}
                </label>
              </div>

              <div className="request-form-email-number">
                <label>
                  Vilken kommun gäller lokalfrågan?
                  <input {...register("stad")} />
                </label>
                <label>
                  Någon specifik stadsdel?
                  <input {...register("part")} />
                </label>
              </div>
            </div>

            <fieldset className="request-form-space-checkbox-group">
              <legend>Vilken typ av lokal gäller det?</legend>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Ateljé"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Ateljé</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Biograf"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Biograf</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Behandlingsrum"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Behandlingsrum</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Butikslokal"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Butikslokal</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Idrottshall"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Idrottshall</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Danslokal"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Danslokal</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Konferenslokal"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Konferenslokal</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Kontor"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Kontor</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Kontorshotell/Co-working"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Kontorshotell/Co-working</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Kyrka"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Kyrka</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Lager/Logistik"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Lager/Logistik</span>
              </label>
              <label className="request-form-checkbox-button">
                <input type="checkbox" value="Kök" {...register("typAvRum")} />
                &nbsp;
                <span>Matsal/Kök</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Eventlokal"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Eventlokal</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Möteslokal"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Möteslokal</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Samtalsrum"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Samtalsrum</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Studio"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Studio</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Teaterlokal"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Teaterlokal</span>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Verkstad"
                  {...register("typAvRum")}
                />
                &nbsp;
                <span>Verkstad</span>
              </label>
              <div className="request-form-space-checkbox-other">
                <label className="request-form-checkbox-button">
                  <input
                    type="checkbox"
                    checked={otherInputs.typAvRum}
                    onChange={(e) => {
                      setOtherInputs((prev) => ({
                        ...prev,
                        typAvRum: e.target.checked,
                      }));
                    }}
                  />
                  &nbsp;
                  <span>Övrigt:</span>
                </label>
                <label className="request-form-name">
                  <input
                    name="descriptionRent"
                    className="form-control"
                    {...register("typAvRumOther")}
                    placeholder="Skriv ditt meddelande"
                    disabled={!otherInputs.typAvRum}
                  />
                </label>
              </div>
            </fieldset>

            <div className="request-form-email-number">
              <label>
                Hur många är ni som ska använda lokalen?
                <input
                  placeholder="Antal"
                  type="number"
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  }}
                  {...register("antalPers")}
                />
              </label>

              <label>
                Ytterligare beskrivning av lokalerna
                <input
                  placeholder="Exempel"
                  {...register("ytterligareBeskrivning")}
                />
              </label>
            </div>

            {/* <div className="request-form-email-number">
              <label>
                Ytterligare beskrivning av lokalerna
                <input
                  placeholder="Exempel"
                  {...register("ytterligareBeskrivning")}
                />
              </label>
            </div> */}

            <fieldset className="request-form-radio-group">
              <legend>Kan ni tänka er att hyra i andrahand?</legend>
              <div className="request-form-sublet-radio">
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Ja"
                    {...register("andraHandsHyra", { defaultChecked: true })}
                  />
                  &nbsp; Ja
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Nej"
                    {...register("andraHandsHyra")}
                  />
                  &nbsp; Nej
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Kanske"
                    {...register("andraHandsHyra")}
                  />
                  &nbsp; Kanske
                </label>
              </div>
            </fieldset>
            {/* <div className="request-form-column"> */}
            <fieldset className="request-form-time-checkbox-group">
              <legend>När ska lokalen hyras?</legend>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Lokalen ska hyras hela tiden"
                  {...register("vilkaTider")}
                />
                &nbsp; Lokalen ska hyras hela tiden
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Vardagar 6-18"
                  {...register("vilkaTider")}
                />
                &nbsp; Vardagar 6-18
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Kvällar 18-24"
                  {...register("vilkaTider")}
                />
                &nbsp; Kvällar 18-24
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Morgnar 00-05"
                  {...register("vilkaTider")}
                />
                &nbsp; Morgnar 00-05
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Helger dagtid"
                  {...register("vilkaTider")}
                />
                &nbsp; Helger dagtid
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Helger kvällstid"
                  {...register("vilkaTider")}
                />
                &nbsp; Helger kvällstid
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="Under lov exempelvis påsklov"
                  {...register("vilkaTider")}
                />
                &nbsp; Under lov exempelvis "påsklov"
              </label>
              <div className="request-form-time-checkbox-other">
                <label className="request-form-checkbox-button">
                  <input
                    type="checkbox"
                    checked={otherInputs.andraTider}
                    onChange={(e) => {
                      setOtherInputs((prev) => ({
                        ...prev,
                        andraTider: e.target.checked,
                      }));
                    }}
                  />
                  &nbsp;
                  <p>Övrigt:</p>
                </label>
                <label className="request-form-name">
                  <input
                    name="descriptionRent"
                    className="form-control"
                    placeholder="Skriv ditt meddelande"
                    {...register("andraTider")}
                    disabled={!otherInputs.andraTider}
                  />
                </label>
              </div>
            </fieldset>
            <div className="request-form-radio-group-column">
              <fieldset className="request-form-radio-group">
                <legend>Vad önskar ni för kontraktsform?</legend>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Hyra per tillfälle"
                    {...register("kontraktForm", { defaultChecked: true })}
                  />
                  &nbsp;
                  <p>Hyra per tillfälle</p>
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Korttidskontrakt (ca 6 månader)"
                    {...register("kontraktForm")}
                  />
                  &nbsp;
                  <p>Korttidskontrakt (ca 6 månader)</p>
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Långtidskontrakt (ca 3-5 år)"
                    {...register("kontraktForm")}
                  />
                  &nbsp;
                  <p>Långtidskontrakt (ca 3-5 år)</p>
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Co-working (arbetsplatser/rum)"
                    {...register("kontraktForm")}
                  />
                  &nbsp;
                  <p>Co-working (arbetsplatser/rum)</p>
                </label>
                <div className="request-form-time-checkbox-other">
                  <label className="request-form-checkbox-other">
                    <input
                      className="request-form-radio-other-radio"
                      type="radio"
                      value="Övrigt"
                      {...register("kontraktForm")}
                    />
                    &nbsp;
                    <p>Övrigt:</p>
                    <input
                      className="request-form-radio-other-text"
                      type="text"
                      {...register("kontraktFormOther")}
                      placeholder="Ange övriga detaljer"
                      disabled={selectedKontraktForm !== "Övrigt"}
                    />
                  </label>
                </div>
                {/* </div> */}
              </fieldset>
              {/* </div> */}
              <fieldset className="request-form-radio-group">
                <legend>Vad har ni för organisationsform?</legend>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Förening"
                    {...register("organisationsTyp")}
                  />
                  &nbsp;
                  <p>Förening</p>
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Aktiebolag"
                    {...register("organisationsTyp")}
                  />
                  &nbsp;
                  <p>Aktiebolag</p>
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Enskild Firma"
                    {...register("organisationsTyp")}
                  />
                  &nbsp;
                  <p>Enskild Firma</p>
                </label>
                <label className="request-form-radio-other">
                  <input
                    className="request-form-radio-other-radio"
                    type="radio"
                    value="Övrigt"
                    {...register("organisationsTyp")}
                  />
                  &nbsp;
                  <p>Övrigt:</p>
                  <input
                    className="request-form-radio-other-text"
                    type="text"
                    {...register("organisationsTypOther")}
                    placeholder="Ange övriga detaljer"
                    disabled={selectedOrganisationsTyp !== "Övrigt"}
                  />
                </label>
              </fieldset>
            </div>

            <fieldset className="request-form-radio-group-budget">
              <legend>Vad har ni för budget?</legend>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="Ingen budget"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>Ingen budget</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="1000 - 4 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>1000 - 4 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="5 000 - 9 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>5 000 - 9 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="10 000 - 19 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>10 000 - 19 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="20 000 - 29 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>20 000 - 29 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="30 000 - 39 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>30 000 - 39 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="40 000 - 49 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>40 000 - 49 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="50 000 - 59 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>50 000 - 59 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="60 000 - 69 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>60 000 - 69 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="70 000 - 79 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>70 000 - 79 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="80 000 - 89 999 kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>80 000 - 89 999 kr/månad</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="90 000+ kr/månad"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>90 000+ kr/månad</p>
              </label>
              <label className="request-form-radio-other">
                <input
                  className="request-form-radio-other-radio"
                  type="radio"
                  value="Övrigt"
                  {...register("prisTyp")}
                />
                &nbsp;
                <p>Övrigt:</p>
                <input
                  className="request-form-radio-other-text"
                  type="text"
                  {...register("prisTypOther")}
                  placeholder="Ange övriga detaljer"
                  disabled={selectedPrisTyp !== "Övrigt"}
                />
              </label>
            </fieldset>

            <fieldset className="request-form-checkbox-group-area">
              <legend>Hur många kvadratmeter gäller det?</legend>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="0-99 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>0-99 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="100 - 199 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>100 - 199 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="200-299 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>200-299 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="300 - 399 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>300 - 399 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="400 - 499 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>400 - 499 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="500 - 599 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>500 - 599 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="600 - 699 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>600 - 699 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="700 - 799 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>700 - 799 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="800 - 899 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>800 - 899 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="900 - 999 kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>900 - 999 kvm</p>
              </label>
              <label className="request-form-checkbox-button">
                <input
                  type="checkbox"
                  value="1000+ kvm"
                  {...register("hurStor")}
                />
                &nbsp;
                <p>1000+ kvm</p>
              </label>
            </fieldset>

            <fieldset className="request-form-radio-group">
              <legend>Vad gäller när det kommer till moms?</legend>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="Lokal där moms läggs på"
                  {...register("moms")}
                />
                &nbsp;
                <p>Lokal där moms läggs på</p>
              </label>
              <label className="request-form-radio">
                <input
                  type="radio"
                  value="Lokal utan moms"
                  {...register("moms")}
                />
                &nbsp;
                <p>Lokal utan moms</p>
              </label>
              <label className="request-form-radio">
                <input type="radio" value="Vet inte" {...register("moms")} />
                &nbsp;
                <p>Vet inte</p>
              </label>
              <label className="request-form-radio-other">
                <input
                  className="request-form-radio-other-radio"
                  type="radio"
                  value="Övrigt"
                  {...register("moms")}
                />
                &nbsp;
                <p>Övrigt:</p>
                <input
                  className="request-form-radio-other-text"
                  type="text"
                  {...register("momsAnnan")}
                  placeholder="Ange övriga detaljer"
                  disabled={selectedmoms !== "Övrigt"}
                />
              </label>
            </fieldset>

            <fieldset className="request-form-radio-group">
              <legend>
                Kan ni tänka er att betala en överlåtelseavgift för en lokal?
              </legend>
              <div className="request-form-sublet-radio">
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Ja"
                    {...register("overlatelseAvgift", { defaultChecked: true })}
                  />
                  &nbsp;
                  <p>Ja</p>
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Nej"
                    {...register("overlatelseAvgift")}
                  />
                  &nbsp;
                  <p>Nej</p>
                </label>
                <label className="request-form-radio">
                  <input
                    type="radio"
                    value="Kanske"
                    {...register("overlatelseAvgift")}
                  />
                  &nbsp;
                  <p>Kanske</p>
                </label>
              </div>
            </fieldset>
            <div className="request-form-footer">
              <label className="request-form-message">
                Har du/ni något önskemål som inte tas upp här?
                <textarea
                  placeholder="Skriv ditt meddelande här"
                  {...register("message")}
                  onInput={autoExpand}
                />
              </label>
              <p>Fält markerade med * är obligatoriska</p>
              <div className="request-form-checkbox">
                <div className="terms-checkbox">
                  <div>
                    <input
                      type="checkbox"
                      {...register("checkbox", { required: true })}
                      aria-invalid={errors.checkbox ? "true" : "false"}
                      aria-describedby={
                        errors.checkbox ? "terms-error" : undefined
                      }
                    />
                    <p className="request-form-checkbox-text">
                      Jag bekräftar att jag har läst och godkänner{" "}
                      <a
                        href="/anvandarvillkor"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        användarvillkoren
                      </a>
                      , inklusive att avtalsvite kan utgå vid bristande
                      återkoppling eller kringgående av plattformen.*
                    </p>
                  </div>
                  {errors.checkbox && (
                    <span id="terms-error" role="alert">
                      Du måste acceptera villkoren
                    </span>
                  )}
                </div>
                <div className="news-checkbox">
                  <input type="checkbox" {...register("nyhetsbrev")} />
                  <p>Jag vill ta emot nyheter och erbjudanden från Vakansa.</p>
                </div>
              </div>
              <div className="request-form-error-msg" role="alert">
                {errorMessage && errorMessage}
              </div>
            </div>
            <div className="request-form-send-button-container">
              <RoundedButton
                className="request-form-send-button"
                color="green"
                label="Skicka"
                size="md"
              />
            </div>
          </form>
        </div>
      </Layout>
    </>
  );
}
